<template>
  <div>
    <v-row class="justify-center">
      <v-wait for="show spinner">
        <v-dialog
          slot="waiting"
          :key="`dialog`"
          content-class="loading-dialog"
          eager
          fullscreen
          persistent
          transition="fade"
          value="true"
        >
          <v-container
            fill-height
            fluid
          >
            <v-row class="justify-center align-center">
              <v-progress-circular
                :size="50"
                color="primary"
                indeterminate
              />
            </v-row>
          </v-container>
        </v-dialog>
      </v-wait>
    </v-row>
    <v-row wrap>
      <v-col sm="9">
        <h1 class="text-h5 mb-2">{{ $t('Inventory Counts') }}</h1>
        <h3
          v-if="inventoryCount.name"
          class="text-subtitle-1"
        >
          <v-icon
            small
            class="mt-n1 mr-2"
          >fal fa-warehouse-alt</v-icon>
          <strong>{{ inventoryCount.name }}</strong>

          <v-icon
            small
            class="mt-n1 mr-1 ml-2"
          >fal fa-calendar-alt</v-icon>
          {{ inventoryCount.created | moment("DD MMMM YYYY HH:mm") }}
        </h3>
        <v-chip
          v-if="inventoryCount.type"
          class="mt-2"
          color="primary"
          label
        >
          <v-icon
            class="mr-2"
            small
          >fal fa-tag</v-icon>
          {{ $t(getTypeLabel(inventoryCount.type)) }}
        </v-chip>
      </v-col>
      <v-col
        sm="3"
        class="text-right"
      >
        <status-chip
          v-if="inventoryCount.status"
          :status-code="inventoryCount.status"
          status-type="inventory-count"
        />
        <template v-if="isPending(inventoryCount)">
          <v-btn
            :loading="$wait.is('cancelling inventorycount')"
            class="white--text"
            color="red"
            @click="cancelInventoryCount">{{ $t('Cancel') }}
          </v-btn>
          <v-btn
            :loading="$wait.is('completing inventorycount')"
            class="white--text"
            color="green"
            @click="completeInventoryCount">{{ $t('Complete') }}
          </v-btn>
        </template>
      </v-col>
      <v-col
        v-show="isPending(inventoryCount)"
        sm="12"
      >
        <v-alert
          class="mt-2 mb-0"
          color="primary"
          icon="fal fa-info-square"
          outlined
          text
        >{{ $t('Add products to the inventory count by scanning or searching them in the box below.') }}</v-alert>
      </v-col>
    </v-row>
    <v-row wrap>
      <v-col :sm="isPending(inventoryCount) ? 9 : 12">
        <vue-position-sticky
          :offset-top="76"
          sticky-class="pb-2"
        >
          <form-wrapper :validator="$v.activeItem">
            <v-row
              v-if="isPending(inventoryCount) && products"
              class="countBar mt-n3"
              style="background-color: #fafafa"
              wrap
            >
              <v-col cols="8">
                <v-autocomplete
                  ref="inputSuggestion"
                  v-model="selection"
                  :disabled="$wait.is('saving count')"
                  :items="filteredProducts"
                  :loading="$wait.is('searching products')"
                  :placeholder="$t('Search {0}', [$t('Products')])"
                  :search-input.sync="searchTerm"
                  autocomplete="off"
                  clearable
                  filled
                  flat
                  hide-details
                  hide-no-data
                  item-text="name"
                  item-value="uuid"
                  no-filter
                  return-object
                  single-line
                  @change="selectSuggestion"
                  @click:clear="resetSuggestion"
                  @input.native="$wait.start('searching products');fuzzyDebounced()"
                />
              </v-col>
              <v-col cols="4">
                <form-group name="newStock">
                  <template v-slot:default="{attrs}">
                    <v-text-field
                      v-comma
                      ref="inputQuantity"
                      v-model.number="activeItem.newStock"
                      v-bind="attrs"
                      :disabled="$wait.is('saving count') || !activeItem.productId"
                      :label="$t('Quantity')"
                      autocomplete="disabled"
                      filled
                      @keypress.enter="saveCount(activeItem)"
                    >
                      <template slot="append-outer">
                        <v-btn
                          :disabled="!activeItem.productId || activeItem.newStock === null"
                          class="white--text btnCount"
                          color="primary"
                          large
                          style="height: 55px"
                          @click="saveCount(activeItem)"
                        >{{ $t('Save') }}</v-btn>
                      </template>
                    </v-text-field>
                  </template>
                </form-group>
              </v-col>
            </v-row>
          </form-wrapper>
        </vue-position-sticky>

        <v-tabs
          v-if="allItems && countedItems && uncountedItems"
          v-model="activeTable"
          slider-color="primary"
        >
          <v-tab>{{ `${$t('All')} (${allItems.length})` }}</v-tab>
          <v-tab>{{ `${$t('Counted')} (${countedItems.length})` }}</v-tab>
          <v-tab>{{ `${$t('Uncounted')} (${uncountedItems.length})` }}</v-tab>
          <v-spacer />
          <v-text-field
            v-model="dataTableSearchTerm"
            :label="$t('Filter {0}', [$t('Results').toLowerCase()])"
            class=" mt-2 pt-0"
            clearable
            dense
            hide-details
            outlined
            single-line
            style="max-width: 400px"
            @input="fuzzyDataTable"
          >
            <v-icon
              slot="prepend-inner"
              class="mr-2"
              size="18px"
              style="margin-top: 2px"
            >far fa-search</v-icon>
          </v-text-field>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-if="isPending(inventoryCount)"
                v-show="[1, 3].indexOf(activeTable + 1) >= 0 && uncountedItems.length"
                :loading="$wait.is(`saving count`)"
                class="ml-3 mr-1 mt-3"
                icon
                ripple
                small
                v-on="on"
                @click="setUncountedItemsToZero"
              >
                <v-icon size="20px">fal fa-minus-circle</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('Set {0} items to {1}', [$t('Uncounted').toLowerCase(), 0]) }}</span>
          </v-tooltip>
          <v-tabs-items v-model="activeTable">
            <v-tab-item class="mt-5">
              <inventory-counts-counter-products
                :active-item="activeItem"
                :inventory-count="inventoryCount"
                :items="allItems"
                @current-items="currentItems = $event"
                @item-removed="fetchInventoryCount().then(() => selectNextItem())"
                @items-removed="fetchInventoryCount"
                @item-selected="selectProductRow($event)"
              />
            </v-tab-item>
            <v-tab-item class="mt-5">
              <inventory-counts-counter-products
                :active-item="activeItem"
                :inventory-count="inventoryCount"
                :items="countedItems"
                @current-items="currentItems = $event"
                @item-removed="fetchInventoryCount().then(() => selectNextItem())"
                @items-removed="fetchInventoryCount"
                @item-selected="selectProductRow($event)"
              />
            </v-tab-item>
            <v-tab-item class="mt-5">
              <inventory-counts-counter-products
                :active-item="activeItem"
                :inventory-count="inventoryCount"
                :items="uncountedItems"
                @current-items="currentItems = $event"
                @item-removed="fetchInventoryCount().then(() => selectNextItem())"
                @items-removed="fetchInventoryCount"
                @item-selected="selectProductRow($event)"
              />
            </v-tab-item>
          </v-tabs-items>
        </v-tabs>

      </v-col>

      <template v-if="isPending(inventoryCount)">
        <v-col
          sm="3"
          mt-4
        >
          <vue-position-sticky :offset-top="77">
            <v-card>
              <v-app-bar
                color="grey lighten-2"
                flat
                height="50px"
              >
                <v-toolbar-title>{{ $t('Your last counted items') }}</v-toolbar-title>
                <v-spacer />
                <v-tooltip
                  v-if="countedItems.length"
                  top
                >
                  <template v-slot:activator="{ on }">
                    <v-icon
                      class="mr-3"
                      small
                      v-on="on"
                      @click="resetInventoryCount"
                    >far fa-trash-alt</v-icon>
                  </template>
                  <span>{{ $t('Reset {0}', [$t('Inventory Count')]) }}</span>
                </v-tooltip>
              </v-app-bar>
              <template v-if="lastCounted && lastCounted.length">
                <perfect-scrollbar>
                  <v-list
                    class="super-dense"
                    dense
                  >
                    <v-slide-x-transition group>
                      <v-list-item
                        v-for="item in lastCounted"
                        :key="item.uuid"
                      >
                        <v-list-item-content>
                          <v-list-item-title><strong>{{ item.newStock }}x</strong> <em>{{ _.get(item, 'product.name') }}</em></v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <v-icon
                                small
                                v-on="on"
                                @click="resetCount(item.productId)"
                              >far fa-trash-alt</v-icon>
                            </template>
                            <span>{{ $t('Remove {0}', [$t('Count')]) }}</span>
                          </v-tooltip>
                        </v-list-item-action>
                      </v-list-item>
                    </v-slide-x-transition>
                  </v-list>
                </perfect-scrollbar>
              </template>
              <template v-else>
                <v-card-text>
                  <div class="text-center">
                    <p class="text-subtitle-1">{{ $t('Items that you count will appear here one-by-one to help you keep track.') }}</p>
                    <v-icon size="50px">fal fa-clipboard-list-check</v-icon>
                  </div>
                </v-card-text>
              </template>
              <v-divider />
              <v-card-actions>
                <v-spacer />
                <v-chip label>{{ `${$t('Counted')}: ${nrOfCountedItems} ${$t('Items').toLowerCase()}` }}</v-chip>
              </v-card-actions>
            </v-card>
          </vue-position-sticky>
        </v-col>
      </template>
      <barcode-scanner
        v-if="!showImportListDialog && !showImportOrderDialog"
        @scan-init="scanProduct($event)"
      />
    </v-row>

    <v-dialog
      v-model="showImportListDialog"
      eager
      max-width="575px"
      persistent
    >
      <span>
        <v-toolbar color="grey lighten-3">
          <v-toolbar-title>{{ $t('Import {0}', [$t('Inventory List')]) }}</v-toolbar-title>
        </v-toolbar>
        <v-card tile>
          <v-card-text>
            <v-alert
              class="text-center mb-6"
              color="primary"
              icon="fal fa-2x fa-qrcode"
              outlined
              prominent
              text
            >
              {{ `${$t('Import a set of products (f.e. all products of a specific brand) by scanning the QR code on a created inventory list.')}` }}
            </v-alert>
            <v-switch
              v-model="importEnabledOnly"
              :label="importEnabledLabel"
              color="primary"
              hide-details
              inset
            />

            <div
              v-show="$wait.is('importing inventory list')"
              class="text-center mt-5"
            >
              <v-progress-circular
                color="primary"
                indeterminate
              />
            </div>
            <barcode-scanner
              v-if="showImportListDialog"
              @scan-init="scanListQRImport($event)"
            />
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn
              class="font-weight-bold"
              color="primary"
              text
              @click="showImportListDialog = false"
            >{{ $t('Close') }}</v-btn>
          </v-card-actions>
        </v-card>
      </span>
    </v-dialog>

    <v-dialog
      v-model="showImportOrderDialog"
      eager
      max-width="575px"
      persistent
    >
      <span>
        <v-toolbar color="grey lighten-3">
          <v-toolbar-title>{{ $t('Import {0}', [$t('Order')]) }}</v-toolbar-title>
        </v-toolbar>
        <v-card tile>
          <v-card-text>
            <v-alert
              class="text-center mb-0"
              color="primary"
              icon="fal fa-2x fa-qrcode"
              outlined
              prominent
              text
            >
              {{ `${$t('Import the products of an incoming order by scanning its QR code.')}` }}
            </v-alert>

            <div
              v-show="$wait.is('importing url list')"
              class="text-center mt-5"
            >
              <v-progress-circular
                color="primary"
                indeterminate
              />
            </div>
            <barcode-scanner
              v-if="showImportOrderDialog"
              @scan-init="scanOrderQRImport($event)"
            />
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn
              class="font-weight-bold"
              color="primary"
              text
              @click="showImportOrderDialog = false"
            >{{ $t('Close') }}</v-btn>
          </v-card-actions>
        </v-card>
      </span>
    </v-dialog>

    <v-dialog
      v-if="showProductChooserDialog"
      v-model="showProductChooserDialog"
      :width="850"
    >
      <v-card tile>
        <v-app-bar
          color="primary"
          dark
          flat
        >
          <v-toolbar-title>{{ $t('Select {0}', [$t('Product')]) }}</v-toolbar-title>
          <v-spacer />

          <v-toolbar-items>
            <v-btn
              dark
              icon
              @click.native="showProductChooserDialog = false"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-app-bar>
        <v-card-text class="pt-4">
          <v-alert
            class="d-inline-block"
            color="orange"
            text
          >
            <v-icon
              class="mr-2"
              color="orange"
            >fal fa-exclamation-triangle</v-icon>
            {{ $t('Multiple products found for barcode: "{0}".', [scannedBarcode]) }} {{ $t('Select the correct product below.') }}
          </v-alert>
          <product-chooser
            :barcode="scannedBarcode"
            @item-selected="selectProduct"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-fab-transition v-if="isPending(inventoryCount)">
      <v-tooltip left>
        <template v-slot:activator="{ on }">
          <v-btn
            v-if="!isIncoming(inventoryCount)"
            v-show="!showImportListDialog"
            bottom
            color="secondary darken-1"
            dark
            fab
            fixed
            right
            v-on="on"
            @click="showImportListDialog = true"
          >
            <v-icon>fal fa-upload</v-icon>
          </v-btn>
          <v-btn
            v-if="isIncoming(inventoryCount)"
            v-show="!showImportOrderDialog"
            bottom
            color="secondary darken-1"
            dark
            fab
            fixed
            right
            v-on="on"
            @click="showImportOrderDialog = true"
          >
            <v-icon>fal fa-upload</v-icon>
          </v-btn>
        </template>
        <span v-if="!isIncoming(inventoryCount)">{{ $t('Import {0}', [$t('Inventory List')]) }}</span>
        <span v-else>{{ $t('Import {0}', [$t('Order')]) }}</span>
      </v-tooltip>
    </v-fab-transition>
  </div>
</template>

<script>
import InventoryCountsCounterProducts from '@/components/inventory-counts/InventoryCountsCounterProducts'
import StatusChip from '@/components/chips/StatusChip'
import BarcodeScanner from '@/components/BarcodeScanner'
import ProductChooser from '@/components/products/ProductChooser'
import inventoryCountService from '@/services/inventory-counts'
import productService from '@/services/products'
import inventoryCountInfoMixin from '@/utils/mixins/inventoryCountInfoMixin'
import validationMixin from '@/utils/mixins/validationMixin'
import { getLabel as getTypeLabel } from '@/utils/types/inventory-count'
import { fuzzyStopWords } from '@/utils/variables'
import allcombinations from 'allcombinations'
import camelcaseKeys from 'camelcase-keys'
import fuzzysort from 'fuzzysort'
import he from 'he'
import _ from 'lodash'
import Vue from 'vue'
import { required, integer, minValue } from 'vuelidate/lib/validators'

export default {
  name: 'InventoryCountsCounter',

  components: {
    BarcodeScanner,
    InventoryCountsCounterProducts,
    StatusChip,
    ProductChooser
  },

  mixins: [validationMixin, inventoryCountInfoMixin],

  props: {
    uuid: {
      required: true,
      type: String
    }
  },

  validations () {
    const validations = {
      activeItem: {
        newStock: { required, integer, minValue: minValue(0) }
      }
    }

    return validations
  },

  data () {
    return {
      activeItem: { productId: null, newStock: null },
      activeTable: null,
      allItems: null,
      currentItems: null,
      dataTableSearchCombinations: [],
      dataTableSearchTerm: '',
      filteredProducts: null,
      importedQRData: null,
      importEnabledLabel: null,
      importEnabledOnly: true,
      inventoryCount: {},
      products: null,
      productStocks: [],
      searchCombinations: [],
      searchTerm: null,
      selection: null,
      showImportListDialog: false,
      showImportOrderDialog: false,
      showProductChooserDialog: false,
      scannedBarcode: null
    }
  },

  computed: {
    countedItems () {
      return this.allItems.filter(item => item.counted !== null)
    },
    uncountedItems () {
      return this.allItems.filter(item => item.counted === null)
    },
    lastCounted () {
      return _.orderBy(this.countedItems, 'counted', 'desc')
      // .slice(0, 15)
    },
    nrOfCountedItems () {
      return this.countedItems.reduce((accumulator, item) => accumulator + item.newStock, 0)
    }
  },

  watch: {
    importEnabledOnly: {
      immediate: true,
      handler (value) {
        this.importEnabledLabel = value ? this.$t('Only products that have been counted in the past will be imported') : this.$t('All products will be imported')
      }
    },
    inventoryCount: {
      handler (value) {
        this.setAllItems()
      }
    }
  },

  created () {
    // fetch the requested report
    this.fetchInventoryCount()
      .then(() => this.fetchCurrentStocks())
      .then(() => this.setAllItems())

    // fetch all products from whole catalog
    productService.list()
      .then((data) => {
        const products = Object.freeze(data.data.products)
        this.filteredProducts = this.products = camelcaseKeys(products, { deep: true })
      })
      .catch(data => { if (data && data.data.message) this.helpers.toastError(data.data.message) })
  },

  methods: {
    addProduct (product) {
      return inventoryCountService.addItem(this.inventoryCount.uuid, product.id)
        .then(data => this.helpers.toastSuccess(data.data.message))
        .finally(() => {
          this.fetchInventoryCount()
            .then(() => {
              const item = this.allItems.find((item) => item.product.id === product.id)
              if (item) {
                this.selectSuggestion(item.product)
              }
            })
        })
    },
    blurTextFields () {
      this.$refs.inputQuantity.blur()
      this.$refs.inputSuggestion.blur()
    },
    cancelInventoryCount () {
      this.$swal({
        title: this.$t('Are you sure?'),
        type: 'warning',
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: this.$t('Yes'),
        showCancelButton: true,
        reverseButtons: true
      })
        .then((result) => {
          if (result.value) {
            this.$wait.start('cancelling inventorycount')
            inventoryCountService.cancelInventoryCount(this.uuid)
              .then((data) => {
                this.helpers.toastSuccess(data.data.message)
                this.$router.push({ name: 'InventoryCountsIndex' })
              })
              .catch(data => { if (data && data.data.message) this.helpers.toastError(data.data.message) })
              .finally(() => this.$wait.end('cancelling inventorycount'))
          }
        })
    },
    completeInventoryCount () {
      this.$wait.start('completing inventorycount')
      inventoryCountService.completeInventoryCount(this.uuid)
        .then((data) => {
          this.helpers.toastSuccess(data.data.message)
          this.$store.dispatch('FETCH_PRODUCT_STOCKS')
          this.$router.push({ name: 'InventoryCountsIndex' })
        })
        .catch(data => { if (data && data.data.message) this.helpers.toastError(data.data.message) })
        .finally(() => this.$wait.end('completing inventorycount'))
    },
    fetchInventoryCount () {
      this.$wait.start('show spinner')
      return inventoryCountService.getInventoryCount(this.uuid)
        .then((data) => {
          this.inventoryCount = camelcaseKeys(data.data.inventory_count, { deep: true })
          return Promise.resolve()
        })
        .finally(() => this.$wait.end('show spinner'))
    },
    fetchCurrentStocks () {
      return productService.stocks()
        .then(data => {
          this.productStocks = camelcaseKeys(data.data.stocks, { deep: true })
          return Promise.resolve()
        })
        .catch(data => { if (data && data.data.message) this.helpers.toastError(data.data.message) })
    },
    fuzzy () {
      // Show all products when nothing searched
      if (this.searchTerm === '' || this.searchTerm === null || typeof this.searchTerm === 'undefined') {
        this.filteredProducts = this.products
        return
      }

      let results = []
      // Generate an array with all possible search combinations
      const searchArray = this.searchTerm
        .toLowerCase()
        .replace(new RegExp('\\b(' + fuzzyStopWords.join('|') + ')\\b', 'g'), '') // remove stopwords
        .replace(/\s\s+/g, ' ') // remove double spaces
        .trim() // remove trailing whitespace
        .split(' ') // split into words array
        .slice(0, 4)
      const generator = allcombinations(searchArray)

      // Do a search on every combination and merge results
      for (let phrase of generator) {
        results = results.concat(fuzzysort.go(phrase.join(' '), this.products, {
          keys: ['brandedName', 'barcode', 'brand.name'],
          allowTypo: false
        }))
      }

      // Sort results by fuzzysort score and return unique objects
      this.filteredProducts = _.uniq(results.sort((a, b) => b.score - a.score).map(x => x.obj))
    },
    fuzzyDebounced: _.debounce(function () {
      this.fuzzy()
      this.$wait.end('searching products')
    }, 750),
    fuzzyDataTable () {
      // Set starting collection to search in
      this.allItems = _.get(this.inventoryCount, 'inventoryCountItems', [])

      // Show all products when nothing searched
      // Don't execute rest of function
      if (this.dataTableSearchTerm === '' || this.dataTableSearchTerm === null || typeof this.dataTableSearchTerm === 'undefined') {
        return
      }

      let results = []
      // Generate an array with all possible search combinations
      const searchArray = this.dataTableSearchTerm
        .toLowerCase()
        .replace(new RegExp('\\b(' + fuzzyStopWords.join('|') + ')\\b', 'g'), '') // remove stopwords
        .replace(/\s\s+/g, ' ') // remove double spaces
        .trim() // remove trailing whitespace
        .split(' ') // split into words array
        .slice(0, 4)
      const generator = allcombinations(searchArray)

      // Do a search on every combination and merge results
      for (let phrase of generator) {
        results = results.concat(fuzzysort.go(phrase.join(' '), this.allItems, {
          keys: ['product.brandedName', 'product.brand.name', 'previousCountedDate'],
          allowTypo: false
        }))
      }

      // Sort results by fuzzysort score and return unique objects
      this.allItems = _.uniq(results.sort((a, b) => b.score - a.score).map(x => x.obj))
    },
    fuzzyDataTableDebounced: _.debounce(function () {
      this.fuzzyDataTable()
    }, 750),
    getCurrentStock (product) {
      if (!product) return '-'
      const productStock = this.productStocks.find(x => x.productId === product.id)
      return _.get(productStock, 'stock')
    },
    getTypeLabel (code) {
      return getTypeLabel(code)
    },
    resetCount (productId) {
      inventoryCountService.resetItemCount(this.uuid, productId)
        .finally(data => this.fetchInventoryCount())
    },
    resetInventoryCount () {
      this.$swal({
        title: this.$t('Are you sure?'),
        type: 'warning',
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: this.$t('Yes'),
        showCancelButton: true,
        reverseButtons: true
      })
        .then((result) => {
          if (result.value) {
            inventoryCountService.resetInventoryCount(this.uuid)
              .finally(data => this.fetchInventoryCount())
          }
        })
    },
    resetSuggestion (focus = true) {
      this.$v.$reset()
      this.selection = null
      this.activeItem.productId = null
      this.activeItem.newStock = null
      if (focus) {
        this.$nextTick(this.$refs.inputSuggestion.focus)
      }
    },
    saveCount (item) {
      if (!this.validate()) return
      this.$wait.start('saving count')
      const productId = item.productId
      inventoryCountService.setItemCount(this.uuid, item.productId, item.newStock)
        .then((data) => {
          this.helpers.toastSuccess(data.data.message)
          this.fetchInventoryCount()
            .then(() => {
              this.resetSuggestion(false)
              this.selectNextItem(productId)
              return Promise.resolve()
            })
        })
        .catch(() => this.fetchInventoryCount())
        .finally(() => this.$wait.end('saving count'))
    },
    scanListQRImport (code) {
      // Check if this is valid JSON
      try {
        this.importedQRData = JSON.parse(he.decode(code))
      } catch (e) {
        return this.helpers.toastError(`${this.$t('Partial {0}', [this.$t('Inventory Count').toLowerCase()])}: ${this.$t('This is not a valid QR Code')}`)
      }
      this.$wait.start('importing inventory list')
      inventoryCountService.importInventoryList(this.uuid, this.importedQRData, this.importEnabledOnly)
        .catch(data => { if (data && data.data.message) this.helpers.toastError(data.data.message) })
        .finally(() => {
          this.fetchInventoryCount()
          this.showImportListDialog = false
          this.$wait.end('importing inventory list')
        })
    },
    scanOrderQRImport (code) {
      this.$wait.start('importing url list')
      inventoryCountService.importUrlList(this.uuid, code)
        .catch(data => { if (data && data.data.message) this.helpers.toastError(data.data.message) })
        .finally(() => {
          this.fetchInventoryCount()
          this.showImportOrderDialog = false
          this.$wait.end('importing url list')
        })
    },
    scanProduct (barcode) {
      this.scannedBarcode = barcode
      this.activeItem.newStock = null

      return productService.countByBarcode(barcode)
        .then(data => {
          if (data.data.count > 1) this.selectDuplicateProduct()
          else {
            return productService.findByBarcode(barcode)
              .then((data) => this.addProduct(data.data.product))
              .catch(data => { if (data && data.data.message) this.helpers.toastError(data.data.message) })
          }
        })
    },
    selectNextItem (currentItemId = null) {
      const uncountedItems = this.currentItems.filter(x => x.newStock === null)
      const currentIndex = currentItemId ? uncountedItems.findIndex((item) => item.productId === currentItemId) : 0
      const nextItem = uncountedItems[currentIndex + 1]

      if (nextItem) {
        this.selectSuggestion(nextItem.product)
      } else {
        // Jump back to top
        if (uncountedItems.length) {
          const nextItem = uncountedItems[0]
          this.selectSuggestion(nextItem.product)
        } else {
          this.resetSuggestion(false)
          this.blurTextFields()
        }
      }
    },
    selectDuplicateProduct () {
      this.showProductChooserDialog = true
    },
    selectProduct (item) {
      this.$wait.start('selecting duplicate product')
      this.addProduct(item)
        .then(() => {
          this.showProductChooserDialog = false
        })
        .finally(() => this.$wait.end('selecting duplicate product'))
    },
    selectProductRow (row) {
      if (this.isPending(this.inventoryCount)) {
        this.activeItem.productId = _.get(row, 'product.id')
        this.activeItem.newStock = row.newStock
        // Update the search field
        this.selection = row.product.name
        // focus the quantity field
        this.$nextTick(this.$refs.inputQuantity.focus)
      }
    },
    selectSuggestion (event) {
      // If autocomplete is cleared with clearable button
      if (!event) {
        this.resetSuggestion()
        return
      }

      const item = this.allItems.find((item) => {
        return _.get(item, 'product.id') === event.id
      })

      // Add item to datatable row if it doesn't exist yet
      // Adding a product calls selectSuggestion when inventoryCount is refreshed so stop executing selectSuggestion by returning nothing
      if (!item) {
        this.addProduct(event)
        return
      }

      this.selectProductRow(item)

      if (!event.name.length) {
        Vue.rollbar.debug(event)
      }
    },
    setAllItems () {
      this.allItems = _.get(this.inventoryCount, 'inventoryCountItems', [])
      this.allItems.forEach((item, index) => {
        // remove items that don't have an attached product anymore
        // product has been deleted
        if (!item.product) {
          this.allItems.splice(index, 1)
        }
        this.$set(this.allItems[index], 'currentStock', this.getCurrentStock(item.product))
      })
    },
    setUncountedItemsToZero () {
      this.$swal({
        title: this.$t('Are you sure?'),
        type: 'warning',
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: this.$t('Yes'),
        showCancelButton: true,
        reverseButtons: true
      })
        .then((result) => {
          if (result.value) {
            this.$wait.start('saving count')
            const itemUuids = this.currentItems.map(item => item.uuid)
            inventoryCountService.setUncountedItemsToZero(this.uuid, itemUuids)
              .then(data => {
                this.helpers.toastSuccess(data.data.message)
                this.fetchInventoryCount()
                  .then(() => this.fuzzyDataTable())
                  .finally(() => this.$wait.end('saving count'))
              })
              .finally(() => this.$wait.end('saving count'))
          }
        })
    }
  }
}
</script>

<style lang="scss" scoped>
  .countBar /deep/ .v-input__append-outer {
    margin-top: 1px !important;
  }

  .btnCount {
    height: 55px;
    margin: 0 2px 0 0;
  }

  .vue-position-sticky /deep/ .countBar {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .ps {
    max-height: 400px;
  }
</style>



// WEBPACK FOOTER //
// src/components/inventory-counts/InventoryCountsCounter.vue